import moment from 'moment';
import { fetchConfigDetailedReport, vaporSignedStorageUrl, putSignedUpload, generateDashboardReport } from '../api/index.js';
import { mapSection } from '../helpers/detailed-report.helper.js'
import { getFromIndexedDb } from '·/utils/indexedDbFileStore'

export async function getConfigDetailedReport(projectId) {
  try {
    const configDetailedReport = await fetchConfigDetailedReport(projectId);

    if (configDetailedReport) {
      let sections = []
      configDetailedReport.sections
        .filter(s => s.customizationType === 'user-editable')
        .forEach(section => {
        sections.push(mapSection(section));
      });

      return sections;
    }

    return []
  } catch (e) {
    console.error("Error manipular configuracion para reporte detallado", e);
    throw e;
  }
}

// Carga ROBUSTA de los apartados (secciones) en shape UI, cubriendo todas las formas del
// config: project.config crudo o backend; `{pages, sections}` (builder) o array legacy;
// secciones ya mapeadas (tienen `print`) o crudas (se mapean con mapSection y se filtran a
// 'user-editable'). Devuelve siempre un array de secciones UI (name/print/disabled/visible/…).
export async function loadReportSections(project) {
  let detailed = project && project.config && project.config.activity_groups
    && project.config.activity_groups.reports && project.config.activity_groups.reports.detailed;
  if (!detailed) detailed = await fetchConfigDetailedReport(project.id);
  if (!detailed) return [];
  const raw = Array.isArray(detailed)
    ? detailed
    : (Array.isArray(detailed.sections) ? detailed.sections : []);
  if (!raw.length) return [];
  const alreadyUi = raw[0] && raw[0].print !== undefined;
  return alreadyUi
    ? raw
    : raw.filter((s) => s.customizationType === 'user-editable').map((s) => mapSection(s));
}

// Config de informe detallado para la vista: del project.config si existe (tal cual), si no
// del backend (mapeado por getConfigDetailedReport). Puede lanzar (el llamador muestra el toast).
export async function loadDetailedConfig(project) {
  const stored = project && project.config && project.config.activity_groups
    && project.config.activity_groups.reports
    && project.config.activity_groups.reports.detailed;
  if (stored) return stored;
  return getConfigDetailedReport(project.id);
}

// Serializa las secciones para la caché (store app/dashboardReport): solo print + imágenes
// de actividad/plano + leyendas. Puro.
export function serializeSectionsForCache(sections) {
  return (sections || []).map((tab) => {
    const tabData = { print: tab.print };
    if (tab.activity_image) tabData.activity_image = tab.activity_image;
    if (tab.drawing_image) tabData.drawing_image = tab.drawing_image;
    if (tab.status_oc) tabData.status_oc = tab.status_oc;
    return tabData;
  });
}

// Fusiona la caché (por índice) sobre las secciones actuales. Muta in-place. Puro.
export function mergeCacheIntoSections(sections, cachedSections) {
  if (!cachedSections) return sections;
  for (let i = 0; i < cachedSections.length; i++) {
    if (sections[i]) Object.assign(sections[i], cachedSections[i]);
  }
  return sections;
}

// Aplica un informe guardado (payload) a las secciones: fechas + ramas por disciplina
// (GENERAL/OC/LEV/ME/MM/SET/BESS/else). Muta `sections` IN-PLACE y devuelve
// { week, weeklyRangeForm }. Las claves que toca (status_oc/with_status/gantt_landscape y,
// por imagen, custom_legends/legends/details) YA existen desde mapSection/initDataImageExtra,
// así que la asignación directa sigue siendo reactiva en Vue 2.7 (no hace falta $set). Puro.
//
// Reglas de restauración que NO se degradan al cargar un informe ya generado:
//  - `with_status` (capacidad = userCanDefineLegend de la config): si la config habilita
//    leyendas, el panel sigue visible aunque el informe guardado no las traiga.
//  - GANTT `gantt_landscape`: solo se restaura la orientación guardada si la config permite
//    cambiarla (gantt_user_can_change_orientation); si no, se mantiene la de config.
//  - Las imágenes en 'amazonaws.com/tmp' (temporales caducadas) se ignoran.
export function applyLoadedReport(sections, report) {
  const weeklyRangeForm = (report.weeklyRange || []).map((date) => {
    const p = date.split('-');
    return new Date(p[2], p[1] - 1, p[0]);
  });

  for (const section of sections) {
    const section_report = Object.values(report.infoTabs).find((s) => {
      if (!s.code) return s.name.toLowerCase() === section.name.toLowerCase();
      return s.code.toLowerCase() === section.code.toLowerCase();
    });

    section.print = section_report.print;
    section.with_activity_document = section_report.with_activity_document;
    section.with_drawing = section_report.with_drawing;

    if (section.name.toLowerCase() === 'general') {
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        section.images[index].image = element.image;
        section.images[index].name = element.name;
        section.images[index].is_activity_document = true;
      }
    }

    else if (section.code === 'OC') {
      if (section_report.with_activity_document) section.activity_image = section_report.activity_image;
      else section.drawing_image = section_report.drawing_image;
      section.status_oc = Array.isArray(section_report.status_oc) ? section_report.status_oc : [];
      section.with_status = section.with_status || section_report.with_status;
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].is_activity_document = true;
        }
      }
    }

    else if (section.code === 'LEV') {
      section.activity_image = null;
      section.drawing_image = null;
      if (section_report.with_activity_document) section.activity_image = section_report.activity_image;
      else if (section_report.with_drawing) section.drawing_image = section_report.drawing_image;
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].is_activity_document = true;
        }
      }
    }

    else if (section.code === 'ME') {
      section.drawing_image = section_report.drawing_image;
      section.status_oc = Array.isArray(section_report.status_oc) ? section_report.status_oc : [];
      section.with_status = section.with_status || section_report.with_status;
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].is_activity_document = true;
        }
      }
    }

    else if (section.code === 'MM') {
      section.drawing_image = section_report.drawing_image;
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].is_activity_document = true;
        }
      }
    }

    else if (section.code === 'SET') {
      if (section_report.with_activity_document) section.activity_image = section_report.activity_image;
      else section.drawing_image = section_report.drawing_image;
      section.status_oc = Array.isArray(section_report.status_oc) ? section_report.status_oc : [];
      section.with_status = section.with_status || section_report.with_status;
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].is_activity_document = true;
        }
      }
    }

    else if (section.code === 'BESS') {
      if (section_report.with_activity_document) section.activity_image = section_report.activity_image;
      else section.drawing_image = section_report.drawing_image;
      section.status_oc = Array.isArray(section_report.status_oc) ? section_report.status_oc : [];
      section.with_status = section.with_status || section_report.with_status;
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].is_activity_document = true;
        }
      }
    }

    else {
      section.activity_image = section_report.activity_image;
      section.drawing_image = section_report.drawing_image;
      section.status_oc = Array.isArray(section_report.status_oc) ? section_report.status_oc : [];
      section.with_status = section.with_status || section_report.with_status;
      if (section.code === 'GANTT' && section.gantt_user_can_change_orientation) {
        section.gantt_landscape = section_report.gantt_landscape ?? section.gantt_landscape;
      }
      for (let index = 0; index < section_report.images.length; index++) {
        const element = section_report.images[index];
        if (!element.image.includes('amazonaws.com/tmp')) {
          section.images[index].image = element.image;
          section.images[index].name = element.name;
          section.images[index].details = element.details || '';
          section.images[index].is_activity_document = true;
          // Restauramos las leyendas específicas por imagen (Gantt).
          section.images[index].custom_legends = !!element.custom_legends;
          section.images[index].legends = Array.isArray(element.legends) ? element.legends : [];
        }
      }
    }
  }

  return { week: report.week, weeklyRangeForm };
}

// ¿El rango semanal es válido (dos fechas)? Puro.
export function isWeeklyRangeValid(range) {
  return Array.isArray(range) && range.length >= 2 && !!range[0] && !!range[1];
}

// Formatea el rango semanal (Date[]) a 'DD-MM-YYYY'. Puro.
export function formatWeeklyRange(weeklyRangeForm) {
  return (weeklyRangeForm || []).map((date) => moment(date).format('DD-MM-YYYY'));
}

// Sube una imagen por Vapor (firma + PUT a S3) y devuelve la data firmada con la extensión.
// `onProgress(pct)` opcional para el porcentaje de subida. La api hace el PUT con instancia
// limpia (ver api/index.js). Sube a /tmp del bucket del entorno automáticamente.
export async function storeImageVapor(file, options = {}) {
  const signed = await vaporSignedStorageUrl({
    bucket: options.bucket,
    contentType: options.contentType || file.type,
    expires: options.expires,
  });
  await putSignedUpload(signed.url, file, signed.headers, (progressEvent) => {
    if (typeof options.onProgress === 'function' && progressEvent && progressEvent.total) {
      options.onProgress(parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total)));
    }
  });
  signed.extension = file.name.split('.').pop();
  return signed;
}

// Arma los infoTabs del informe: por cada sección imprimible, las imágenes ya-documento van
// tal cual y las subidas por el usuario se suben via `storeImageFn` (Vapor). Async.
// `s3bucket` se usa para la ruta directa a la imagen.
export async function buildInfoTabs(sections, storeImageFn, s3bucket) {
  const infoTabs = [];
  for (const reportSection of (sections || [])) {
    const reportImages = [];
    if (reportSection.print) {
      for (let i = 0; i < reportSection.images.length; i++) {
        const img = reportSection.images[i];
        if (!img.image) continue;
        if (img.is_activity_document) {
          reportImages.push({ name: img.name, key: '', image: img.image, custom_legends: !!img.custom_legends, legends: img.legends || [] });
        } else {
          // No se usa FormData: un POST > 1MB puede dar 413 (Entity Too Large) → subida por Vapor.
          const data = await storeImageFn(img.image, {});
          // la url que devuelve aws da "SignatureDoesNotMatch"; usamos ruta directa a la imagen.
          data.urlManual = 'https://' + s3bucket + '/' + data.key;
          if (data) {
            reportImages.push({ name: img.name, key: data.uuid, image: data.urlManual, details: img.details || '', custom_legends: !!img.custom_legends, legends: img.legends || [] });
          }
        }
      }
    }
    infoTabs.push({ ...reportSection, images: reportImages, subactivities: [], documents: [] });
  }
  return infoTabs;
}
// Capturas del Gantt guardadas (IndexedDB) listas para usar: cada una { date, file(File) }.
// El acceso a datos + el envoltorio blob→File viven aquí; la vista solo muestra y emite.
export async function loadGanttCaptures(projectId) {
  let list = [];
  try {
    list = await getFromIndexedDb('gantt_captures:' + projectId);
  } catch (e) {
    list = [];
  }
  return (list || []).map((c) => ({
    date: c.date,
    file: new File([c.blob], 'gantt_' + c.date + '.webp', { type: 'image/webp' }),
  }));
}

// Genera el informe detallado (PDF): formatea el rango, arma los infoTabs (sube imágenes por
// Vapor) y llama al backend. Orquestación de negocio fuera de la vista (que solo valida,
// gestiona el estado de carga y notifica).
export async function generateDetailedReport(projectId, { report_sections, weeklyRangeForm, week, s3bucket }) {
  const weeklyRange = formatWeeklyRange(weeklyRangeForm);
  const infoTabs = await buildInfoTabs(report_sections, (file, opts) => storeImageVapor(file, opts), s3bucket);
  await generateDashboardReport(projectId, { infoTabs, weeklyRange, week });
}
